import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { toast } from 'material-react-toastify';

import Button from '@/components/shared/Button/Button';
import DynamicSVGColor from '@/components/shared/DynamicSVGColor/DynamicSVGColor';
import { FilterSelect } from '@/components/shared/FilterSelect/FilterSelect';
import { Loading } from '@/components/shared/Loading/Loading';
import { ContentStatus, JourneyHub, JourneySummaryHub } from '@addmile/backend-client';
import * as Sentry from '@sentry/react';

import { CONTENT_STATUS } from '../Content/const';
import CantPublishModal from './components/CantPublishModal/CantPublishModal';
import JourneyCard from './components/JourneyCard/JourneyCard';
import JourneyConfirmModal from './components/JourneyConfirmModal/JourneyConfirmModal';
import TakeOfflineModal from './components/TakeOfflineModal/TakeOfflineModal';
import { NEW_JOURNEY_ID, journeyPath } from './const';
import { count } from './count';
import {
  journeyErrorMessage,
  useChangeJourneyStatus,
  useDeleteJourney,
  useDuplicateJourney,
  useJourneys,
} from './services/JourneyService';

const ALL_STATUSES = '';

const Journeys = () => {
  const navigate = useNavigate();
  const { data: journeys = [], isLoading, error } = useJourneys();
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);

  const changeStatus = useChangeJourneyStatus();
  const duplicateJourney = useDuplicateJourney();
  const deleteJourney = useDeleteJourney();
  const [pendingDelete, setPendingDelete] = useState<JourneySummaryHub>();
  const [pendingOffline, setPendingOffline] = useState<{
    journey: JourneySummaryHub;
    status: ContentStatus;
  }>();
  const [notPublished, setNotPublished] = useState<JourneyHub>();

  const shown = statusFilter
    ? journeys.filter((journey) => journey.status === statusFilter)
    : journeys;
  const statusOptions = [
    { value: ALL_STATUSES, label: `All Statuses (${journeys.length})` },
    ...CONTENT_STATUS.map(({ value, label, icon, color }) => ({
      value,
      label: `${label} (${journeys.filter((journey) => journey.status === value).length})`,
      icon,
      color,
    })),
  ];

  const applyStatus = async (journey: JourneySummaryHub, status: ContentStatus) => {
    try {
      const saved = await changeStatus.mutateAsync({ id: journey.id, status });
      if (saved.status !== status) {
        setNotPublished(saved);
        return;
      }
      toast.success(`"${saved.title}" is ${status} now`);
    } catch (error) {
      Sentry.captureException(error);
      toast.error(journeyErrorMessage(error, 'Failed to change the status'));
    }
  };

  const handleStatusChange = (journey: JourneySummaryHub, status: ContentStatus) => {
    if (status === journey.status) return;
    if (journey.status === ContentStatus.Online) {
      setPendingOffline({ journey, status });
      return;
    }
    applyStatus(journey, status);
  };

  const handleCopy = async (id: string) => {
    try {
      const copy = await duplicateJourney.mutateAsync(id);
      toast.success(`"${copy.title}" created as a draft`);
    } catch (error) {
      Sentry.captureException(error);
      toast.error(journeyErrorMessage(error, 'Failed to duplicate the journey'));
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    const { id } = pendingDelete;
    setPendingDelete(undefined);

    try {
      await deleteJourney.mutateAsync(id);
      toast.success('Journey deleted');
    } catch (error) {
      Sentry.captureException(error);
      toast.error(journeyErrorMessage(error, 'Failed to delete the journey'));
    }
  };

  const renderList = () => {
    if (isLoading) {
      return <Loading />;
    }

    if (error) {
      return (
        <div className="text-center py-10 text-red-600 bg-red-50 border border-red-200 rounded-lg">
          {journeyErrorMessage(error, 'Failed to load journeys')}
        </div>
      );
    }

    if (!shown.length) {
      return (
        <div className="rounded-2xl bg-white-100 py-10 text-center">
          <p className="text-lg font-semibold text-neutral-90">
            {journeys.length ? `No ${statusFilter} journeys` : 'No journeys yet'}
          </p>
          {!journeys.length && (
            <p className="text-sm text-neutral-50">Create the first one — it starts as a draft.</p>
          )}
        </div>
      );
    }

    return (
      <div className="grid gap-3">
        {shown.map((journey) => (
          <JourneyCard
            key={journey.id}
            journey={journey}
            onEdit={(id) => navigate(journeyPath(id))}
            onCopy={handleCopy}
            onDelete={setPendingDelete}
            onStatusChange={handleStatusChange}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="w-full max-w-[1084px] p-6">
      <JourneyConfirmModal
        isOpen={!!pendingDelete}
        title="Delete journey?"
        confirmLabel="Delete"
        danger
        onConfirm={handleConfirmDelete}
        onClose={() => setPendingDelete(undefined)}
      >
        <b>{pendingDelete?.title || 'Untitled journey'}</b> and everything in it —{' '}
        {count(pendingDelete?.milesCount ?? 0, 'mile')},{' '}
        {count(pendingDelete?.stepsCount ?? 0, 'step')},{' '}
        {count(pendingDelete?.blocksCount ?? 0, 'block')} — will be deleted. This can’t be undone.
      </JourneyConfirmModal>
      <TakeOfflineModal
        journeyTitle={pendingOffline?.journey.title ?? ''}
        status={pendingOffline?.status}
        onConfirm={() => {
          setPendingOffline(undefined);
          if (pendingOffline) applyStatus(pendingOffline.journey, pendingOffline.status);
        }}
        onClose={() => setPendingOffline(undefined)}
      />
      <CantPublishModal
        journey={notPublished}
        onClose={() => setNotPublished(undefined)}
        onSelect={() => notPublished && navigate(journeyPath(notPublished.id))}
      />

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-neutral-90">Journeys</h1>
        <Button
          label="New Journey"
          className="!w-fit px-5"
          prefixIcon={<DynamicSVGColor icon="/icons/plus.svg" className="w-5 h-5 !bg-white-100" />}
          onClick={() => navigate(journeyPath(NEW_JOURNEY_ID))}
        />
      </div>

      <div className="mb-4 w-fit">
        <FilterSelect
          label="Status"
          value={statusFilter}
          onChange={setStatusFilter}
          options={statusOptions}
        />
      </div>
      {renderList()}
    </div>
  );
};

export default Journeys;
